import { FormEvent, useState } from "react"
import { Head, router, usePage } from "@inertiajs/react"
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel,
  FormGroup, InputLabel, MenuItem, Paper, Select, TextField, Typography
} from "@mui/material"
import AdminLayout from "../../../layouts/AdminLayout"

type Topic = { id: number; name: string }
type Speaker = { id: number; name: string; voice: string; gender: string | null }
type Props = { topics: Topic[]; speakers: Speaker[] }

export default function NewConversation({ topics, speakers }: Props) {
  const { errors } = usePage<{ errors?: Record<string, string> }>().props
  const [prompt, setPrompt] = useState("")
  const [topicId, setTopicId] = useState<number | "">("")
  const [participantIds, setParticipantIds] = useState<number[]>([])
  const [submitting, setSubmitting] = useState(false)

  const toggleParticipant = (id: number) =>
    setParticipantIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    router.post(
      "/admin/conversations",
      { conversation: { prompt, topic_id: topicId === "" ? null : topicId, participant_speaker_ids: participantIds } },
      { onStart: () => setSubmitting(true), onFinish: () => setSubmitting(false) }
    )
  }

  return (
    <AdminLayout title="New conversation">
      <Head title="New conversation" />
      <Typography variant="h5" sx={{ mb: 2 }}>New conversation</Typography>
      {errors?.base && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="conversation-error">{errors.base}</Alert>
      )}
      <Paper component="form" onSubmit={onSubmit} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Describe the scenario</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The AI drafts a short beginner-level Japanese dialogue from your description.
          You can review it and refine it with feedback before accepting.
        </Typography>
        <TextField
          fullWidth multiline rows={4} required value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          label="Scenario"
          placeholder="e.g. Two friends meet at a station and decide where to eat lunch."
          slotProps={{ htmlInput: { "data-testid": "conversation-prompt" } }}
        />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="topic-label">Topic (optional)</InputLabel>
          <Select
            labelId="topic-label" label="Topic (optional)" value={topicId}
            onChange={(e) => setTopicId(e.target.value === "" ? "" : Number(e.target.value))}
            data-testid="conversation-topic"
          >
            <MenuItem value="">— None —</MenuItem>
            {topics.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Participants</Typography>
          {speakers.length === 0 ? (
            <Alert severity="warning" data-testid="cast-empty">
              No speakers exist yet — seed the catalog before generating a conversation.
            </Alert>
          ) : (
            <FormGroup data-testid="participants">
              {speakers.map((s) => (
                <FormControlLabel
                  key={s.id}
                  control={
                    <Checkbox
                      checked={participantIds.includes(s.id)}
                      onChange={() => toggleParticipant(s.id)}
                    />
                  }
                  label={s.gender ? `${s.name} (${s.gender})` : s.name}
                />
              ))}
            </FormGroup>
          )}
          <Typography variant="caption" color="text.secondary">
            Select at least two participants — the AI casts the dialogue from them.
          </Typography>
        </Box>
        <Button
          type="submit" variant="contained" sx={{ mt: 3 }}
          disabled={submitting || !prompt.trim() || participantIds.length < 2}
          data-testid="generate-draft"
        >
          {submitting ? <CircularProgress size={20} /> : "Generate draft"}
        </Button>
      </Paper>
    </AdminLayout>
  )
}
